import React, { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { isInviteCode } from 'constants/play';
import PlayNotFound from './PlayNotFound';
import PlayLandingPage from './screens/Landing/PlayLandingPage';
import HostCreatePage from './screens/Host/HostCreatePage';
import HostSignInPage from './screens/Host/HostSignInPage';
import PlayNameStepPage from './screens/Auth/PlayNameStepPage';
import HostConsolePage from './screens/Host/HostConsolePage';
import PlayProfilePage from './screens/Profile/PlayProfilePage';
import DiscordCompletePage from './screens/Auth/DiscordCompletePage';
import TournamentHomePage from './screens/Tournament/TournamentHomePage';
import LivePage from './screens/Live/LivePage';
import StandingsPage from './screens/Tournament/StandingsPage';
import DeckPage from './screens/Player/DeckPage';
import ReportPage from './screens/Player/ReportPage';

/**
 * The /play route tree (Asana 1220000146037011, Decisions 1, 3, 24, 27).
 * Fixed segments are declared before `:code`; `:code` is validated against the
 * invite alphabet and PLAY_RESERVED_SEGMENTS before anything fetches, so a
 * reserved word can never be read as a code.
 */

export const useIsAuthenticated = (): boolean => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  return Boolean(token || user);
};

/**
 * Same contract as routes/ProtectedRoute, but the sign-in tab is opened
 * directly (a Play visitor is never asked to join the beta first).
 * First intent wins on the stored returnTo. With `fallback` (Eric's ruling 31,
 * 2026-10-30) a signed-out visitor sees that Play screen in place instead of
 * being sent to /welcome; the fallback's own entries store the returnTo.
 */
export const RequirePlayAuth: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ children, fallback }) => {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const hasFallback = fallback !== undefined;

  useEffect(() => {
    if (!isAuthenticated && !hasFallback) {
      if (!ImportSessionStorage.getReturnTo()) {
        ImportSessionStorage.setReturnTo(location.pathname + location.search);
      }
      navigate('/welcome?tab=signin', { replace: true });
    }
  }, [isAuthenticated, hasFallback, navigate, location.pathname, location.search]);

  if (!isAuthenticated) return hasFallback ? <>{fallback}</> : null;
  return <>{children}</>;
};

export interface TournamentOutletContext {
  code: string;
}

/** Validates the code segment, then renders the nested screen with the code in the outlet context. */
const TournamentRoute: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  if (!isInviteCode(code)) {
    return <PlayNotFound reason="invalid" />;
  }
  return <Outlet context={{ code } satisfies TournamentOutletContext} />;
};

const PlayRoutes: React.FC = () => (
  <Routes>
    <Route index element={<PlayLandingPage />} />
    <Route
      path="host"
      element={
        <RequirePlayAuth fallback={<HostSignInPage />}>
          <HostCreatePage />
        </RequirePlayAuth>
      }
    />
    <Route
      path="me"
      element={
        <RequirePlayAuth>
          <PlayProfilePage />
        </RequirePlayAuth>
      }
    />
    <Route path="auth/discord/complete" element={<DiscordCompletePage />} />
    <Route
      path="auth/name"
      element={
        <RequirePlayAuth>
          <PlayNameStepPage />
        </RequirePlayAuth>
      }
    />
    <Route path="auth/*" element={<Navigate replace to="/play" />} />
    <Route path=":code" element={<TournamentRoute />}>
      <Route index element={<TournamentHomePage />} />
      <Route path="live" element={<LivePage />} />
      <Route
        path="standings"
        element={
          <RequirePlayAuth>
            <StandingsPage />
          </RequirePlayAuth>
        }
      />
      <Route
        path="deck"
        element={
          <RequirePlayAuth>
            <DeckPage />
          </RequirePlayAuth>
        }
      />
      <Route
        path="report"
        element={
          <RequirePlayAuth>
            <ReportPage />
          </RequirePlayAuth>
        }
      />
      <Route
        path="host"
        element={
          <RequirePlayAuth>
            <HostConsolePage />
          </RequirePlayAuth>
        }
      />
      <Route path="*" element={<PlayNotFound reason="invalid" />} />
    </Route>
    <Route path="*" element={<PlayNotFound reason="invalid" />} />
  </Routes>
);

export default PlayRoutes;
